/**
 * Comptoir print — the governed HTML artifact (GET /results/comptes-rendus/:id/imprimer) re-rendered
 * from the frozen lines, shown in a sandboxed iframe preview with print + open-in-new-tab actions.
 * Gated on results.compterendu.print. The print route returns RAW html (the only non-envelope route)
 * and only for an `emis` CR, so this panel refuses (with a clear message) when the CR isn't emitted.
 * All 5 states: forbidden (parent route + the canPrint guard), offline, loading, error, empty.
 */
import { useRef } from "react";
import { Button, EmptyState, ErrorState, LoadingState, OfflineState } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { ForbiddenState } from "@/ui";
import type { CompteRendu } from "../types";
import { RESULTS_CAPS } from "../caps";
import { usePrintArtifact } from "../hooks";

export function ComptoirPrint({ cr, active }: { cr: CompteRendu; active: boolean }) {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const canPrint = useCan(RESULTS_CAPS.print);
  const frameRef = useRef<HTMLIFrameElement>(null);

  const isEmitted = cr.etat === "emis";
  const { loading, error, html, reload } = usePrintArtifact(cr.id, active && canPrint && isEmitted);

  if (!canPrint) {
    return <ForbiddenState requiredCapabilities={[RESULTS_CAPS.print]} />;
  }
  if (!isEmitted) {
    return <EmptyState glyph="○" title={t("results.print.notEmitted")} />;
  }
  if (!online && !html) {
    return <OfflineState onRetry={reload} cached={false} />;
  }
  if (loading) {
    return <LoadingState label={t("results.print.loading")} />;
  }
  if (error) {
    return <ErrorState message={error} onRetry={reload} />;
  }
  if (!html) {
    return <EmptyState title={t("results.print.empty")} action={<Button onClick={reload}>{t("results.action.refresh")}</Button>} />;
  }

  const printFrame = () => {
    const win = frameRef.current?.contentWindow;
    if (win) {
      win.focus();
      win.print();
    }
  };

  const openNewTab = () => {
    // Re-render into a standalone tab via the authenticated markup we already hold (no token in URL).
    const w = window.open("", "_blank", "noopener");
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
      <div style={{ display: "flex", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
        <Button onClick={printFrame} leadingIcon="⎙" disabled={!online && !html}>
          {t("results.print.print")}
        </Button>
        <Button variant="secondary" onClick={openNewTab} leadingIcon="↗">
          {t("results.print.openNewTab")}
        </Button>
        <Button variant="ghost" onClick={reload} disabled={!online} leadingIcon="↻">
          {t("results.action.refresh")}
        </Button>
      </div>
      <div
        style={{
          border: "1px solid var(--nv-line)",
          borderRadius: "var(--nv-r-md)",
          overflow: "hidden",
          background: "var(--nv-white)",
        }}
      >
        <iframe
          ref={frameRef}
          title={t("results.print.frame")}
          srcDoc={html}
          sandbox="allow-same-origin allow-modals"
          style={{ width: "100%", height: 520, border: "none", display: "block" }}
        />
      </div>
    </div>
  );
}
